import React from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Plus, Search, Bell } from 'lucide-react';

// Mock data for charts
const subscriptionData = Array.from({ length: 30 }, (_, i) => ({
  value: Math.floor(Math.random() * 50) + 10
}));

const orderData = Array.from({ length: 30 }, (_, i) => ({
  value: Math.floor(Math.random() * 500) + 200
}));

const revenueData = Array.from({ length: 30 }, (_, i) => ({
  value: Math.floor(Math.random() * 2000) + 500
}));

const campaigns = [
  {
    id: 1,
    icon: '🎯',
    title: '10 Simple steps to revolutionise workflows with our product',
    status: 'Draft',
    startDate: 'Not Started',
    lastUpdated: 'Apr 10, 2023',
    team: ['/avatars/1.png', '/avatars/2.png', '/avatars/3.png']
  },
  {
    id: 2,
    icon: '🚀',
    title: 'Boost your performance: start using our amazing product',
    status: 'In Progress',
    startDate: 'Jun 1, 2023',
    endDate: 'Aug 1, 2023',
    lastUpdated: 'July 10, 2023',
    team: ['/avatars/4.png']
  },
  {
    id: 3,
    icon: '💫',
    title: 'The power of our product: A new era in SaaS',
    status: 'Archived',
    endDate: 'Jun 11, 2023',
    lastUpdated: 'Apr 10, 2023',
    team: ['/avatars/5.png', '/avatars/6.png', '/avatars/7.png', '/avatars/8.png']
  }
];

export const Analytics = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-semibold text-gray-900">Analytics</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search"
                  className="w-64 pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
              <button className="p-2 rounded-lg hover:bg-gray-100">
                <Bell className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Revenue Overview */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Your total revenue</h2>
            <div className="flex space-x-4">
              <button className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                Select Dates
              </button>
              <button className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                Filters
              </button>
            </div>
          </div>
          <div className="text-4xl font-bold text-[#4B6BFB] mb-8">$90,239.00</div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* New Subscriptions */}
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">22</h3>
                  <p className="text-sm text-gray-500">New subscriptions</p>
                </div>
                <span className="flex items-center text-green-500 text-sm">
                  <ArrowUpRight className="h-4 w-4" />
                  15%
                </span>
              </div>
              <div className="h-20">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={subscriptionData}>
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#4B6BFB"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-sm text-gray-500 mt-2">compared to last week</p>
            </div>

            {/* New Orders */}
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">320</h3>
                  <p className="text-sm text-gray-500">New orders</p>
                </div>
                <span className="flex items-center text-red-500 text-sm">
                  <ArrowDownRight className="h-4 w-4" />
                  4%
                </span>
              </div>
              <div className="h-20">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={orderData}>
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#4B6BFB"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-sm text-gray-500 mt-2">compared to last week</p>
            </div>

            {/* Average Order Revenue */}
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">$1,080</h3>
                  <p className="text-sm text-gray-500">Avg. order revenue</p>
                </div>
                <span className="flex items-center text-green-500 text-sm">
                  <ArrowUpRight className="h-4 w-4" />
                  8%
                </span>
              </div>
              <div className="h-20">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueData}>
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#4B6BFB"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-sm text-gray-500 mt-2">compared to last week</p>
            </div>
          </div>
        </div>

        {/* Recent Campaigns */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Recent campaigns</h2>
            <a href="#" className="text-[#4B6BFB] hover:underline">
              View All
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Campaign Status Cards */}
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-medium text-gray-500">Draft</h3>
                <span className="text-lg font-semibold">2</span>
              </div>
              {campaigns
                .filter((campaign) => campaign.status === 'Draft')
                .map((campaign) => (
                  <div
                    key={campaign.id}
                    className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer mb-3"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{campaign.icon}</span>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                          {campaign.title}
                        </h4>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">
                            Start: {campaign.startDate}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-medium text-gray-500">In Progress</h3>
                <span className="text-lg font-semibold">2</span>
              </div>
              {campaigns
                .filter((campaign) => campaign.status === 'In Progress')
                .map((campaign) => (
                  <div
                    key={campaign.id}
                    className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer mb-3"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{campaign.icon}</span>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                          {campaign.title}
                        </h4>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">
                            {campaign.startDate} - {campaign.endDate}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-medium text-gray-500">Archived</h3>
                <span className="text-lg font-semibold">1</span>
              </div>
              {campaigns
                .filter((campaign) => campaign.status === 'Archived')
                .map((campaign) => (
                  <div
                    key={campaign.id}
                    className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer mb-3"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{campaign.icon}</span>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                          {campaign.title}
                        </h4>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">
                            Ended: {campaign.endDate}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Add Campaign Button */}
          <button className="mt-6 flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 hover:border-gray-300 hover:text-gray-600">
            <Plus className="h-5 w-5 mr-2" />
            Add campaign
          </button>
        </div>
      </main>
    </div>
  );
};